import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import logoImage from "../../Pages/img/abrechnungsystemLogos_internal.jpg";
import "./logo.css";
import { ChevronLeft, ChevronRightOutlined } from "@mui/icons-material";

import { useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import FlexBetween from "./FlexBetween";
import { filterNavigationItems } from "./NavigationItems";

import { getNumberFromString, getStorageItem, capitalizeFirstLetter } from "../../Common/Utility";

const SideBarMenu = ({
  isNonMobile,
  isSidebarOpen,
  setIsSidebarOpen,
  drawerWidth,
  onRequestLogout,
}) => {
  const [activeOption, setActiveOption] = useState("");

  const { pathname } = useLocation();
  const loginData = JSON.parse(localStorage.getItem("login"));
  const theme = useTheme();
  const [boxWidth, setBoxWidth] = useState("290px");
  const [isNavOpen, setIsNavOpen] = useState(true);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserName, setCurrentUserName] = useState("");
  useEffect(() => {
    (async () => {
      const admin = await getStorageItem("login", "isAdmin");
      setIsAdmin(!!admin);
      setFilteredItems(filterNavigationItems(!!admin));
      const name = await getStorageItem("login", "userName");
      setCurrentUserName(name ? capitalizeFirstLetter(name) : "");
    })();
  }, [isAdmin]);

  useEffect(() => {
    setActiveOption(pathname.substring(1));
  }, [pathname]);

  useEffect(() => {
    setBoxWidth(drawerWidth);
    if (getNumberFromString(drawerWidth) <= 100) setIsNavOpen(false);
    else setIsNavOpen(true);
  }, [drawerWidth]);

  if (!loginData || (typeof loginData === "object" && Object.keys(loginData).length === 0)) {
    return null;
  }

  return (
    <Box>
      {(isSidebarOpen || isNavOpen) && (
        <Drawer
          open={isSidebarOpen || isNavOpen}
          onClose={() => setIsSidebarOpen(!isSidebarOpen || !isNavOpen)}
          variant="persistent"
          anchor="left"
          sx={{
            width: drawerWidth - 1,
            overflowX: "hidden",
            "& .MuiDrawer-paper": {
              width: drawerWidth - 1,
              overflowX: "hidden",
              backgroundColor: theme.palette.background.alt,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box width={boxWidth} sx={{ flex: "1 1 auto", overflow: "auto" }}>
            {/* Title and Chevron Button */}
            <Box>
              <FlexBetween gap="0.5rem" color={theme.palette.secondary.main}>
                <Box>
                  {/* <Typography variant="h6" fontWeight="bold">
                    AbrechnungsSystem
                  </Typography> */}
                  <img
                    src={logoImage}
                    alt="Willkommen to AbrechnungsSystem"
                    className="logo"
                  />
                </Box>
                {!isNonMobile && (
                  <IconButton
                    onClick={() =>
                      setIsSidebarOpen(!isSidebarOpen || !isNavOpen)
                    }
                  >
                    <ChevronLeft />
                  </IconButton>
                )}
              </FlexBetween>
            </Box>
            {/* List of Sidebar Buttons */}

            <List>
              {filteredItems.map(({ text, icon, link, isLogout }) => {
                // Section header (no icon)
                if (!icon && isNavOpen) {
                  return (
                    <Typography
                      key={text}
                      sx={{ margin: "1.5rem 0 0.8rem 2.7rem" }}
                      fontWeight="bold"
                    >
                      {text}
                    </Typography>
                  );
                }
                // Logout action (no navigation)
                if (isLogout) {
                  return (
                    <ListItem key={text} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          onRequestLogout?.();
                        }}
                        sx={{
                          color: theme.palette.secondary[100],
                          "&:hover": {
                            backgroundColor: theme.palette.secondary[300],
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            ml: "1.8rem",
                            color: theme.palette.secondary[200],
                          }}
                        >
                          {icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            currentUserName
                              ? `${text} (${currentUserName})`
                              : text
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                }
                const lowerCaseText = (link || "").toLowerCase();
                const path = link ? "/" + link : "/";

                return (
                  <ListItem key={text} disablePadding>
                    <ListItemButton
                      component={Link}
                      to={path}
                      onClick={() => {
                        setActiveOption(lowerCaseText);
                      }}
                      sx={{
                        fontWeight:
                          activeOption === lowerCaseText ? "bold" : "normal",
                        color:
                          activeOption === lowerCaseText
                            ? theme.palette.primary.main
                            : link === "ControlPannel"
                            ? "red"
                            : link === "AuditLogs"
                            ? "green"
                            : link === "SystemUpdates"
                            ? "lightgreen"
                            : link === "UserProfile"
                            ? "blue"
                            : theme.palette.secondary[100],
                        backgroundColor:
                          activeOption === lowerCaseText
                            ? "#B7E2FC"
                            : "transparent",
                        "&:hover": {
                          backgroundColor:
                            activeOption === lowerCaseText &&
                            theme.palette.secondary[300],
                          opacity: activeOption === lowerCaseText && 0.8,
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          ml: "1.8rem",
                          color:
                            activeOption === lowerCaseText
                              ? theme.palette.primary[600]
                              : theme.palette.secondary[200],
                        }}
                      >
                        {icon}
                      </ListItemIcon>
                      <ListItemText primary={text} />
                      {activeOption === lowerCaseText && (
                        <ChevronRightOutlined />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>

          {/* Current user name */}
          {currentUserName && isNavOpen && (
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                py: 1.5,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                ({currentUserName})
              </Typography>
            </Box>
          )}
        </Drawer>
      )}
    </Box>
  );
};

export default SideBarMenu;
